/**
 * Shared HTTP client for the Peregrine Ops API.
 *
 * `apiFetch` attaches the caller's Cognito ID token as a bearer credential.
 * Every `/ops/*` route is authorized by a Cognito authorizer, so an ops call
 * without a token is rejected at the edge. Each API module previously carried
 * its own unauthenticated fetch helper, which is how the internal control plane
 * ended up publicly readable — keep ops calls going through here.
 *
 * `publicFetch` is the deliberate exception, for the customer-facing onboarding
 * flow (`/onboard/*`), where the customer holds a one-time token and has no
 * Cognito session. Use it ONLY for routes that are intentionally public.
 */
import { CONFIG } from '../config';
import { getIdToken } from '../lib/auth-service';

async function parseError(response: Response): Promise<never> {
  const body = await response.json().catch(() => ({}));
  const msg =
    (body as { error?: { message?: string }; message?: string })?.error?.message ||
    (body as { message?: string })?.message ||
    `API error: ${response.status}`;
  throw new Error(msg);
}

/** Authenticated ops API call. Throws if the user has no valid session. */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${CONFIG.API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });

  if (!response.ok) return parseError(response);
  return response.json() as Promise<T>;
}

/**
 * Unauthenticated call for intentionally public routes (`/onboard/*`).
 * Do not use for `/ops/*`.
 */
export async function publicFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${CONFIG.API_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  });
  if (!response.ok) return parseError(response);
  return response.json() as Promise<T>;
}
